import { modelLocationTopContent } from "@/app/[brand]/[model]/pageStaticData";
import { formatPrice } from "@/app/utilities/helper";
import { convertModelTags, findSpecValue } from "@/app/utilities/modelTagConverter";
import {
    EMI_DEFAULT_INTEREST_RATE,
    EMI_DEFAULT_TENURE,
    formatEmiCurrency,
    getDefaultDownPayment,
} from "@/lib/utils/emi";
import { getOnRoadPrice } from "@/utils/onRoadPrice";

export const formatLakhPrice = (value?: number) => {
    if (!value) return "";
    return String(formatPrice(value, { includeUnit: true })).replace(/₹\s+/g, "₹").replace(/\*+$/, "");
};

export const formatRupeeFull = (value?: number) => {
    if (!value) return "";
    return `₹ ${Math.round(value).toLocaleString("en-IN")}`;
};

export const CITY_PRICE_INSURANCE_TOOLTIP_TEXT =
    "The insurance premium is determined by the vehicle's engine capacity or battery size and varies between metro and non-metro cities. Additionally, the amount may differ from one dealer to another based on the insurance provider and applicable commissions.";

export const CITY_PRICE_TOTAL_ON_ROAD_TOOLTIP_TEXT =
    "On-Road Price excludes 1% TCS (applicable on ex-showroom > ₹10 lakh)";

const formatRupee = (value?: number) => (value ? `₹${formatEmiCurrency(value)}` : "");

const joinProse = (items: string[]) => {
    if (items.length <= 1) return items[0] || "";
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

const normalizeTransmission = (value: string) => {
    const trans = value.toLowerCase();
    if (!trans) return "";
    if (/automatic|amt|cvt|dct|dca|\bat\b/.test(trans)) return "automatic";
    if (/manual/.test(trans)) return "manual";
    return trans;
};

const automaticLabel = (value: string) => {
    const trans = value.toLowerCase();
    if (/dca/.test(trans)) return "DCA";
    if (/amt/.test(trans)) return "AMT";
    if (/cvt/.test(trans)) return "CVT";
    if (/dct/.test(trans)) return "DCT";
    return "";
};

const cheaper = (current: any, next: any) =>
    !current || Number(next?.price ?? Infinity) < Number(current?.price ?? Infinity) ? next : current;

const splitHtmlParagraphs = (value: string) =>
    String(value || "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .split(/\n+/)
        .map((line) => line.replace(/\s+/g, " ").trim())
        .filter(Boolean);

export type CityPriceDescription = {
    intro: string;
    sections: { title: string; items: { label: string; text: string }[] }[];
    breakupTitle: string;
    breakupItems: { label: string; text: string }[];
    compare: string;
    tableTitle: string;
    minOnRoad: number;
    maxOnRoad: number;
};

const emptyDescription = (intro = ""): CityPriceDescription => ({
    intro,
    sections: [],
    breakupTitle: "",
    breakupItems: [],
    compare: "",
    tableTitle: "",
    minOnRoad: 0,
    maxOnRoad: 0,
});

export const buildCityPriceDescription = ({
    model,
    variants,
    cityName,
    isUpcoming,
    isElectric,
}: {
    model: any;
    variants: any[];
    cityName: string;
    isUpcoming: boolean;
    isElectric: boolean;
}): CityPriceDescription => {
    if (isUpcoming) {
        return emptyDescription(splitHtmlParagraphs(convertModelTags(modelLocationTopContent.upcoming, model)).join(" "));
    }

    const brand = model?.brand_name || model?.brand?.name || "";
    const fullName = `${brand} ${model?.name || ""}`.trim();
    const locationInfo = model?.locationInfo;
    const fuels = new Set<string>();
    const transmissions = new Set<string>();
    const autoTypes = new Set<string>();
    const cheapest: Record<string, any> = {};
    const priced: any[] = [];
    let minVariant: any;
    let maxVariant: any;

    for (const item of variants) {
        if (item?.status === "discontinued") continue;

        const price = Number(item?.price) || 0;
        if (price > 0) {
            priced.push(item);
            if (!minVariant || price < Number(minVariant.price)) minVariant = item;
            if (!maxVariant || price > Number(maxVariant.price)) maxVariant = item;
        }

        const transRaw = findSpecValue(item, "transmission_type") || item?.transmission || "";
        const transNorm = normalizeTransmission(transRaw);
        if (transNorm) transmissions.add(transNorm);
        const autoType = automaticLabel(transRaw);
        if (autoType) autoTypes.add(autoType);

        const listedFuels = Array.isArray(item?.fuel_types)
            ? item.fuel_types
            : item?.fuel_types
                ? [item.fuel_types]
                : [];
        const fuelValues = [...listedFuels, item?.fuel_type, findSpecValue(item, "fuel_type")];
        for (const fuel of fuelValues) {
            const key = String(fuel || "").trim().toLowerCase();
            if (!key) continue;
            fuels.add(key);
            if (key.includes("petrol")) cheapest.petrol = cheaper(cheapest.petrol, item);
            if (key.includes("diesel")) cheapest.diesel = cheaper(cheapest.diesel, item);
            if (key.includes("cng")) cheapest.cng = cheaper(cheapest.cng, item);
            if (key.includes("electric")) cheapest.electric = cheaper(cheapest.electric, item);
        }
        if (transNorm === "manual") cheapest.manual = cheaper(cheapest.manual, item);
        if (transNorm === "automatic") cheapest.auto = cheaper(cheapest.auto, item);
    }

    for (const fuel of model?.fuel_types || []) {
        const key = String(fuel || "").trim().toLowerCase();
        if (key) fuels.add(key);
    }
    for (const trans of model?.transmissions || []) {
        const transNorm = normalizeTransmission(String(trans || ""));
        if (transNorm) transmissions.add(transNorm);
        const autoType = automaticLabel(String(trans || ""));
        if (autoType) autoTypes.add(autoType);
    }

    const onRoadOf = (item: any) => (item && locationInfo ? getOnRoadPrice(item, locationInfo) : null);
    const baseResult = onRoadOf(minVariant);
    const minOnRoad = baseResult?.onRoadPrice || 0;
    const maxOnRoad = minVariant === maxVariant ? minOnRoad : onRoadOf(maxVariant)?.onRoadPrice || 0;

    if (!minOnRoad) {
        return emptyDescription(`The price of ${fullName} in ${cityName} is not revealed yet.`);
    }

    const baseName = model?.listing_summary?.overall?.cheapest?.variant_name || minVariant?.name || "";
    const topName = model?.listing_summary?.overall?.expensive?.variant_name || maxVariant?.name || "";
    const variantCount = priced.length || Number(model?.variant_count) || 0;
    const isSingle = variantCount <= 1;
    const fuelCount = fuels.size || (isElectric ? 1 : 0);
    const transCount = transmissions.size;
    const colourCount = Number(model?.colour_count || model?.color_count || (model?.colours || model?.colors || []).length) || 0;
    const showroomCount = Number(model?.dealer_count || model?.showroom_count || model?.dealers?.length || model?.showrooms?.length) || 0;
    const extras = `${colourCount ? ` and ${colourCount} colour options` : ""}${
        showroomCount ? `, and you can buy it from any of the ${showroomCount} ${brand} showrooms across ${cityName}` : ""
    }`;
    const emi = baseResult?.defaultEmi
        ? ` EMI of ${fullName} in ${cityName} starts from just ₹${formatEmiCurrency(baseResult.defaultEmi)} per month over a ${EMI_DEFAULT_TENURE}-month tenure at ${EMI_DEFAULT_INTEREST_RATE}% interest with a down payment of ${formatRupee(getDefaultDownPayment(minOnRoad))}.`
        : "";
    const autoLabel = autoTypes.size ? `${[...autoTypes].join(" and ")} variants` : "Automatic variants";
    const fuelList = [...fuels];
    const transList = [...transmissions];
    const fuelProse = joinProse(fuelList.map((fuel) => (fuel === "cng" ? "CNG" : fuel)));
    const transOptions = transCount > 1 ? ` with ${joinProse(transList)} options` : "";
    const sections: { title: string; items: { label: string; text: string }[] }[] = [];

    let intro = "";
    if (isSingle) {
        const trans = transList[0];
        const transClause = trans ? ` with ${/^[aeiou]/.test(trans) ? "an" : "a"} ${trans} transmission` : "";
        intro = `${fullName} on road price in ${cityName} is ${formatLakhPrice(minOnRoad)} for the ${baseName}. The on road price includes the ex-showroom price plus RTO charges and insurance. The ${fullName} is available in a single ${isElectric ? "electric" : fuelList[0] || "petrol"} variant${transClause}${extras}.${emi}`;
    } else {
        const availability = fuelCount > 1
            ? `variants across ${fuelProse}${transOptions}`
            : `${fuelList[0] || "petrol"} variants${transOptions}`;
        intro = `${fullName} on road price in ${cityName} starts at ${formatLakhPrice(minOnRoad)} for the base ${baseName} and goes up to ${formatLakhPrice(maxOnRoad)} for the top ${topName}. The on road price includes the ex-showroom price plus RTO charges and insurance. The ${fullName} is available in ${variantCount} ${availability}${extras}.${emi}`;
    }

    const cheapestOnRoad = (key: string) => onRoadOf(cheapest[key])?.onRoadPrice || 0;

    if (!isSingle && fuelCount <= 1 && transCount >= 2) {
        const manualOnRoad = cheapestOnRoad("manual");
        const automaticOnRoad = cheapestOnRoad("auto");
        if (manualOnRoad && automaticOnRoad) {
            sections.push({
                title: `${fullName} Price by Transmission:`,
                items: [
                    { label: `${fullName} Manual Price`, text: `Starts at ${formatLakhPrice(manualOnRoad)} on road in ${cityName}.` },
                    { label: `${fullName} Automatic Price`, text: `${autoLabel} start from ${formatLakhPrice(automaticOnRoad)} on road in ${cityName}.` },
                ],
            });
        }
    }

    if (!isSingle && fuelCount > 1) {
        const items = (["petrol", "diesel", "cng", "electric"] as const)
            .flatMap((key) => {
                const value = cheapestOnRoad(key);
                if (!value) return [];
                const label = key === "cng" ? "CNG" : key[0].toUpperCase() + key.slice(1);
                return [{ label: `${fullName} ${label} Price`, text: `Starts at ${formatLakhPrice(value)} on road in ${cityName}.` }];
            });
        const automaticOnRoad = cheapestOnRoad("auto");
        if (automaticOnRoad) {
            items.push({ label: `${fullName} Automatic Price`, text: `${autoLabel} start from ${formatLakhPrice(automaticOnRoad)} on road in ${cityName}.` });
        }
        if (items.length) {
            sections.push({ title: `${fullName} Price by Fuel Type and Transmission:`, items });
        }
    }

    const exShowroom = formatLakhPrice(Number(minVariant?.price || model?.listing_summary?.overall?.cheapest?.price || 0));
    const rtoTotal = baseResult?.rtoEstimate?.total || 0;
    const insuranceTotal = baseResult?.insuranceEstimate?.total || 0;
    const breakupItems = [
        exShowroom
            ? {
                label: "Ex-Showroom Price",
                text: isSingle
                    ? `${exShowroom}, the base cost of the car before taxes and registration.`
                    : `Starts at ${exShowroom}, the base cost of the car before taxes and registration.`,
            }
            : null,
        rtoTotal
            ? { label: "RTO Charges", text: `Around ${formatRupee(rtoTotal)}, covering road tax and registration in ${cityName}.` }
            : null,
        insuranceTotal
            ? { label: "Insurance", text: `Approximately ${formatRupee(insuranceTotal)} for comprehensive coverage.` }
            : null,
        {
            label: "On Road Price",
            text: isSingle
                ? `Total payable price of ${formatLakhPrice(minOnRoad)}.`
                : `Total payable price starting from ${formatLakhPrice(minOnRoad)} for the base variant.`,
        },
    ].filter((item): item is { label: string; text: string } => Boolean(item));

    let compare = "";
    if (!isSingle && baseName && topName) {
        priced.sort((a, b) => Number(a.price) - Number(b.price));
        const mid1 = priced.length >= 4 ? priced[Math.floor(priced.length / 3)] : null;
        const mid2 = priced.length >= 4 ? priced[Math.floor((priced.length * 2) / 3)] : null;
        const mid1OnRoad = onRoadOf(mid1)?.onRoadPrice || 0;
        const mid2OnRoad = onRoadOf(mid2)?.onRoadPrice || 0;
        const detailed = fuelCount > 1 && mid1?.name && mid2?.name && mid1OnRoad && mid2OnRoad && mid1.name !== baseName && mid2.name !== topName;
        compare = detailed
            ? `The ${baseName} is the most affordable variant at ${formatLakhPrice(minOnRoad)}, while the ${mid1.name} is priced at ${formatLakhPrice(mid1OnRoad)}. For added features, the ${mid2.name} comes at ${formatLakhPrice(mid2OnRoad)} and the top ${topName} at ${formatLakhPrice(maxOnRoad)}. Compare all variants below and choose the ${fullName} that fits your budget.`
            : `The ${baseName} is the most affordable variant at ${formatLakhPrice(minOnRoad)}, while the top ${topName} is priced at ${formatLakhPrice(maxOnRoad)}. Compare all variants below and choose the ${fullName} that fits your budget.`;
    }

    return {
        intro,
        sections,
        breakupTitle: `${fullName} On Road Price Breakup in ${cityName}:`,
        breakupItems,
        compare,
        tableTitle: `${fullName} On Road Price List in ${cityName} (All Variants)`,
        minOnRoad,
        maxOnRoad,
    };
};
